import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslate } from '../hooks/useTranslate';
import NewsCard from './NewsCard';
import PhotoGallery from './PhotoGallery';
import { OptimizedImage } from './ui/optimized-image';

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [displayCount, setDisplayCount] = useState(2); // Afficher 2 news au départ
  const [totalNews, setTotalNews] = useState(0);
  const { currentLanguage } = useLanguage();

  // Traductions
  const { translatedText: titleTranslated } = useTranslate("Les News");
  const { translatedText: descriptionTranslated } = useTranslate("Restez informés des dernières actualités de l'Association Royale des Descendants des Lignages de Bruxelles");
  const { translatedText: noNewsTranslated } = useTranslate("Aucune actualité disponible pour le moment.");
  const { translatedText: articleLabelTranslated } = useTranslate("Article");
  const { translatedText: byTranslated } = useTranslate("par");
  const { translatedText: readArticleTranslated } = useTranslate("Lire l'article complet");
  const { translatedText: loadMoreTranslated } = useTranslate("Charger plus de news");
  
  // Traductions pour le modal
  const { translatedText: modalTitleTranslated } = useTranslate(selectedArticle?.title || "");
  const { translatedText: modalContentTranslated } = useTranslate(selectedArticle?.content || "");

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/news/published`);
      if (response.ok) {
        const data = await response.json();
        setNews(data.news || []);
        setTotalNews((data.news || []).length);
      }
    } catch (error) {
      console.error('Erreur chargement news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 2);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getExcerpt = (content) => {
    const div = document.createElement("div");
    div.innerHTML = content;
    const text = div.textContent || div.innerText || '';
    return text.length > 150 ? text.substring(0, 150) + '...' : text;
  };

  const handleReadArticle = (article) => {
    setSelectedArticle(article);
  };

  const handleCloseModal = () => {
    setSelectedArticle(null);
  };

  if (loading) {
    return (
      <section id="news" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" 
                 style={{ borderBottomColor: 'rgb(196, 150, 96)' }}></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="news" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 
            className="text-4xl font-bold mb-4"
            style={{ 
              fontFamily: '"Lora", serif',
              color: 'rgb(196, 150, 96)'
            }}
          >
            {titleTranslated}
          </h2>
          <p 
            className="text-gray-600 max-w-2xl mx-auto"
            style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
          >
            {descriptionTranslated}
          </p>
        </div>

        {news.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500" style={{ fontFamily: '"Lora", serif' }}>
              {noNewsTranslated}
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-8">
            {news.slice(0, displayCount).map((article) => (
              <NewsCard
                key={article.id}
                article={article}
                onReadArticle={handleReadArticle}
                formatDate={formatDate}
                getExcerpt={getExcerpt}
                articleLabelTranslated={articleLabelTranslated}
                byTranslated={byTranslated}
                readArticleTranslated={readArticleTranslated}
              />
            ))}
          </div>
        )}
        
        {/* Load More Button - N'afficher que s'il y a plus de news à charger */}
        {displayCount < totalNews && (
          <div className="text-center mt-12">
            <Button 
              size="lg" 
              className="text-white hover:opacity-90 px-8 py-3 text-lg"
              onClick={handleLoadMore}
              style={{ 
                fontFamily: '"Lora", serif',
                backgroundColor: 'rgb(196, 150, 96)'
              }}
            >
              {loadMoreTranslated}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Modal pour afficher l'article complet */}
        {selectedArticle && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={handleCloseModal}
          >
            <div 
              className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 
                  className="text-2xl font-bold"
                  style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
                >
                  {currentLanguage === 'fr' ? selectedArticle.title : (modalTitleTranslated || selectedArticle.title)}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              
              <div className="px-6 py-4">
                <div className="flex items-center gap-4 text-gray-500 text-sm mb-4">
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    {selectedArticle.author}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {formatDate(selectedArticle.published_at)}
                  </div>
                </div>
                
                {selectedArticle.cover_image && (
                  <OptimizedImage 
                    src={selectedArticle.cover_image} 
                    alt={selectedArticle.title}
                    className="w-full h-64 object-cover rounded-lg mb-6"
                    priority={true}
                  />
                )}
                
                <div 
                  className="prose prose-lg max-w-none"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                  dangerouslySetInnerHTML={{ 
                    __html: currentLanguage === 'fr' ? selectedArticle.content : (modalContentTranslated || selectedArticle.content)
                  }}
                />

                {/* Album Photo */}
                {selectedArticle.photo_album && selectedArticle.photo_album.length > 0 && (
                  <PhotoGallery 
                    photos={selectedArticle.photo_album} 
                    columns={3}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default News;